import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"
import { Button, Modal } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { CheckboxHeader } from "simplydo/react/components/CheckboxHeader"

import { useAppSelector } from "store"
import util from "utils/utils"

type ConfirmAnnouncementModalProps = {
  handleConfirm: (sendAnnouncement: Array<string>) => void
  modalOpen: boolean
  announcements: Array<OpenAPI.Schemas["Announcement"]>
  ideaCount: number
  onClose: () => void
}

const ConfirmAnnouncementModal = ({
  handleConfirm,
  modalOpen,
  announcements,
  ideaCount,
  onClose,
}: ConfirmAnnouncementModalProps) => {
  const { t } = useTranslation()
  const params = useParams()
  const user = useAppSelector((state) => state.user)
  const [announcementsToTrigger, setAnnouncementsToTrigger] = useState<Array<string>>(
    announcements.map((announcement) => announcement._id),
  )

  useEffect(() => {
    setAnnouncementsToTrigger(announcements.map((announcement) => announcement._id))
  }, [announcements])

  const toUserConditionMap = {
    userThatTriggered: "User that triggered the announcement",
    customUsers: "A custom set of users",
    ideaOwner: `Users that own or collaborate on the ${t("generic.idea")}`,
  }
  return (
    <Modal mountNode={document.getElementById("semantic-modal-mount-node")} open={modalOpen} onClose={() => onClose()}>
      <Modal.Header>
        {util.pluralise(
          ideaCount,
          t("common:capitalise", { key: "generic.idea" }),
          t("common:capitalise", { key: "generic.ideas" }),
          false,
        )}{" "}
        was moved
      </Modal.Header>
      <Modal.Content>
        <p>
          There{" "}
          {util.pluralise(
            announcements.length,
            "is a project lane announcement",
            "are project lane announcements",
            false,
          )}{" "}
          configured when {t("generic.ideas")} are moved to this lane.{" "}
          {util.hasPermission(user, "challenge.viewSettings", params.id) ||
          util.hasPermission(user, "org.manageChallenges", user?.ownerOrganisation?._id) ? (
            <>
              You can review announcements in the{" "}
              <Link to={`/challenges/${params.id}/settings/announcements`} target="_blank">
                {t("generic.challenge")} settings
              </Link>
              .
            </>
          ) : null}
        </p>
        <h5>Lane {util.pluralise(announcements.length, "announcement", "announcements", false)}</h5>
        {announcements.map((announcement) => (
          <CheckboxHeader
            header={announcement.subject}
            as="h4"
            description={
              <span>
                {/* @ts-ignore */}
                Recipients: <b>{toUserConditionMap[announcement.toUserCondition || "userThatTriggered"]}</b>
              </span>
            }
            key={announcement._id}
            checked={announcementsToTrigger.includes(announcement._id)}
            onChange={() => {
              if (announcementsToTrigger.includes(announcement._id)) {
                setAnnouncementsToTrigger(announcementsToTrigger.filter((a) => a !== announcement._id))
              } else {
                setAnnouncementsToTrigger([...announcementsToTrigger, announcement._id])
              }
            }}
          />
        ))}
        <br />
        <p>
          Do you want to inform the recipients above about the progression of the moved{" "}
          {util.pluralise(ideaCount, t("generic.idea"), t("generic.ideas"), false)}?
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => handleConfirm([])}>
          Skip {util.pluralise(announcementsToTrigger.length, "announcement", "announcements", false)}
        </Button>
        <Button primary onClick={() => handleConfirm(announcementsToTrigger)} disabled={!announcementsToTrigger.length}>
          Send {util.pluralise(announcementsToTrigger.length, "announcement", "announcements", false)}
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default ConfirmAnnouncementModal
